"use client";

import React, { useState, useEffect, Suspense } from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { ArrowRight, Globe, ShieldCheck, Cpu } from "lucide-react";
import p5Types from "p5";
const Sketch = React.lazy(() => import("react-p5").then(mod => ({
  default: mod.default
})));
const SketchLoader = () => <div data-section-id="36224" className="flex items-center justify-center w-full h-full min-h-[400px] bg-secondary/5 rounded-3xl animate-pulse">
    <span className="text-secondary/50 font-mono text-sm">Cultivating Vision...</span>
  </div>;
export default function PoweringYourDigitalPulse() {
  const [isHovered, setIsHovered] = useState(false);
  const setup = (p5: p5Types, canvasParentRef: Element) => {
    const parent = canvasParentRef.parentElement;
    const w = parent ? parent.clientWidth : 600;
    const h = parent ? parent.clientHeight : 600;
    p5.createCanvas(w, h, p5.WEBGL).parent(canvasParentRef);
  };
  const draw = (p5: p5Types) => {
    p5.clear();
    const targetRotX = (p5.mouseY - p5.height / 2) * 0.001;
    const targetRotY = (p5.mouseX - p5.width / 2) * 0.001;
    p5.rotateX(p5.frameCount * 0.005);
    p5.rotateY(p5.frameCount * 0.005);
    p5.rotateX(targetRotX);
    p5.rotateY(targetRotY);
    p5.ambientLight(40, 20, 10);
    p5.pointLight(239, 68, 68, 200, -200, 200);
    p5.pointLight(251, 146, 60, -200, 200, 200);
    p5.specularMaterial(250);
    p5.shininess(80);
    p5.fill(239, 68, 68, 60);
    p5.stroke(251, 191, 36, 90);
    p5.strokeWeight(0.8);
    const scaleFactor = Math.min(p5.width, p5.height) / 500;
    p5.scale(scaleFactor);
    p5.torus(140, 50, 40, 24);
    p5.push();
    p5.rotateX(p5.PI / 2);
    p5.stroke(255, 255, 255, 40);
    p5.noFill();
    p5.sphere(85, 16, 16);
    p5.pop();
  };
  const windowResized = (p5: p5Types) => {
    const parent = p5.canvas.parentElement?.parentElement;
    if (parent) {
      p5.resizeCanvas(parent.clientWidth, parent.clientHeight);
    }
  };
  return <section id="connecting-continents-delivering-quality" className="relative w-full min-h-[90vh] flex items-center overflow-hidden bg-background dark">
      {}
      <div className="absolute inset-0 pointer-events-none z-0 opacity-[0.15]" style={{
      backgroundImage: `
            linear-gradient(to right, var(--secondary) 1px, transparent 1px),
            linear-gradient(to bottom, var(--secondary) 1px, transparent 1px)
          `,
      backgroundSize: '60px 60px',
      maskImage: 'radial-gradient(circle at center, black 30%, transparent 100%)'
    }} />

      {}
      <div className="absolute top-[-20%] right-[-10%] w-[600px] h-[600px] bg-secondary/10 rounded-full blur-[120px] pointer-events-none" />
      <div className="absolute bottom-[-20%] left-[-10%] w-[500px] h-[500px] bg-accent/10 rounded-full blur-[100px] pointer-events-none" />

      <div className="container mx-auto px-6 relative z-10 h-full py-20 lg:py-0">
        <div className="flex flex-col lg:flex-row items-center h-full gap-12 lg:gap-0">
          
          {}
          <div className="w-full lg:w-[60%] flex flex-col justify-center space-y-8 text-center lg:text-left">
            
            {}
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-accent/10 border border-secondary/20 w-fit mx-auto lg:mx-0 backdrop-blur-md">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-secondary opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-secondary"></span>
              </span>
              <span className="text-xs font-bold text-secondary tracking-widest uppercase">
                Premium Global Logistics
              </span>
            </div>

            {}
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold tracking-tighter text-foreground leading-[1.05]">
              Bridging <br className="hidden lg:block" />
              Continents Through <span className="text-secondary relative inline-block">
                Quality
                <svg className="absolute w-full h-4 -bottom-2 left-0 text-secondary/30" viewBox="0 0 100 10" preserveAspectRatio="none">
                  <path d="M0 5 L 100 5" stroke="currentColor" strokeWidth="6" fill="none" strokeLinecap="round" />
                </svg>
              </span>
            </h1>

            {}
            <p className="text-lg md:text-xl text-muted-foreground leading-relaxed max-w-2xl mx-auto lg:mx-0 font-normal">
              Cosmic EXIM LLC facilitates high-quality global trade between the USA and India. From authentic regional spices to sustainable eco-ware, we deliver excellence across every border we cross.
            </p>

            {}
            <div className="flex flex-col sm:flex-row items-center gap-4 justify-center lg:justify-start pt-4">
              <Link to="/ProductsPage">
                <Button size="lg" className="h-16 px-10 text-lg rounded-full bg-secondary text-secondary-foreground shadow-xl shadow-secondary/20 hover:shadow-secondary/40 hover:scale-105 transition-all duration-300 font-bold">
                  Explore Products
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              
              <Link to="/ContactPage">
                <Button variant="outline" size="lg" className="h-16 px-10 text-lg rounded-full border-2 border-secondary/50 text-foreground hover:bg-secondary/10 backdrop-blur-sm transition-all">
                  Get In Touch
                </Button>
              </Link>
            </div>

            {}
            <div className="pt-10 flex items-center justify-center lg:justify-start gap-10 text-muted-foreground/80">
              <div className="flex items-center gap-2.5">
                <ShieldCheck className="h-6 w-6 text-secondary" />
                <span className="text-sm font-semibold tracking-wide uppercase">Authentic</span>
              </div>
              <div className="flex items-center gap-2.5">
                <Cpu className="h-6 w-6 text-secondary" />
                <span className="text-sm font-semibold tracking-wide uppercase">Sustainable</span>
              </div>
              <div className="flex items-center gap-2.5">
                <Globe className="h-6 w-6 text-secondary" />
                <span className="text-sm font-semibold tracking-wide uppercase">Global</span>
              </div>
            </div>
          </div>

          {}
          <div className="w-full lg:w-[40%] h-[450px] lg:h-[650px] relative flex items-center justify-center" onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)}>
            {}
            <div className="absolute inset-0 bg-gradient-to-br from-secondary/5 via-transparent to-accent/10 rounded-[4rem] lg:rounded-l-[5rem] lg:rounded-r-none pointer-events-none border-l border-t border-secondary/10" />
            
            {}
            <div className="w-full h-full relative z-10 cursor-grab active:cursor-grabbing">
              <Suspense fallback={<SketchLoader />}>
                <Sketch setup={setup} draw={draw} windowResized={windowResized} className="w-full h-full" />
              </Suspense>
            </div>

            {}
            <div className="absolute top-1/4 right-8 p-5 bg-card/90 backdrop-blur-xl border border-border/40 rounded-3xl shadow-2xl animate-bounce duration-[3500ms] hidden lg:block">
              <div className="flex items-center gap-4">
                <div className="h-12 w-12 rounded-2xl bg-secondary/20 flex items-center justify-center text-secondary">
                  <svg className="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
                <div>
                  <p className="text-xs font-bold text-secondary uppercase tracking-tighter">Sourcing Origin</p>
                  <p className="text-sm font-bold text-foreground">Hyderabad, India</p>
                </div>
              </div>
            </div>

             <div className="absolute bottom-1/4 left-4 p-5 bg-card/90 backdrop-blur-xl border border-border/40 rounded-3xl shadow-2xl animate-bounce duration-[4500ms] hidden lg:block">
              <div className="flex items-center gap-4">
                <div className="h-12 w-12 rounded-2xl bg-accent/20 flex items-center justify-center text-accent-foreground">
                  <svg className="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <p className="text-xs font-bold text-accent-foreground uppercase tracking-tighter">Trade Quality</p>
                  <p className="text-sm font-bold text-foreground">Premium Grade</p>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>
    </section>;
}